import * as React from 'react';
import { Alert } from 'reactstrap';
import { useSelector } from 'react-redux';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { getEntitiesDataItems } from '../../../store/entities/selectors';
import { ClientEntity } from '../../../store/entities/state';
import ClientForm from '../../../components/Forms/ClientForm/ClientForm';
import { ClientData } from '../../../models';
import { AnalysisResult } from './useAnalyseCSV';
import { ColumnMapping } from './autoMap';
import { ImportOptions } from './ImportOptions';
import { parseRow } from './parseRow';

type RowStatus = 'create' | 'update' | 'skip-empty' | 'skip-existing';

interface Props {
    result: AnalysisResult;
    mapping: ColumnMapping;
    options: ImportOptions;
}

function useRowStatuses(result: AnalysisResult, mapping: ColumnMapping, options: ImportOptions): RowStatus[] {
    const existingClients = useSelector(getEntitiesDataItems<ClientEntity>('clients'));

    return React.useMemo(() => {
        const nameSet = new Set(
            existingClients.map(c => c.name?.trim().toLowerCase()).filter(Boolean)
        );
        const nameIndices = mapping.fieldMap['name'] ?? [];
        return result.allRows.map(row => {
            const name = nameIndices.map(i => row[i] ?? '').find(v => v.trim())?.trim() ?? '';
            if (!name) return 'skip-empty';
            if (options.skipExistingOnly && nameSet.has(name.toLowerCase())) return 'skip-existing';
            return nameSet.has(name.toLowerCase()) ? 'update' : 'create';
        });
    }, [existingClients, mapping, options.skipExistingOnly, result.allRows]);
}

const STATUS_COLORS: Record<RowStatus, string> = {
    create: 'var(--success-color)',
    update: 'var(--appBlue)',
    'skip-empty': 'var(--text-muted)',
    'skip-existing': 'var(--text-muted)',
};

const MAX_SKIPPED_SHOWN = 100;

const PreviewStep: React.FC<Props> = ({ result, mapping, options }) => {
    const t = useTranslations();
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [previewData, setPreviewData] = React.useState<ClientData | null>(null);
    const [showSkippedList, setShowSkippedList] = React.useState(false);
    const rowStatuses = useRowStatuses(result, mapping, options);
    const total = result.allRows.length;
    const existingClients = useSelector(getEntitiesDataItems<ClientEntity>('clients'));

    const navigate = React.useCallback((delta: number) => {
        setCurrentIndex(i => Math.max(0, Math.min(total - 1, i + delta)));
    }, [total]);

    React.useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') navigate(-1);
            if (e.key === 'ArrowRight') navigate(1);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [navigate]);

    // Re-parse whenever the row index changes
    React.useEffect(() => {
        const row = result.allRows[currentIndex];
        if (row) setPreviewData(parseRow(row, result.headers, mapping));
    }, [currentIndex, result, mapping]);

    const counts = React.useMemo(() => ({
        create: rowStatuses.filter(s => s === 'create').length,
        update: rowStatuses.filter(s => s === 'update').length,
        skipEmpty: rowStatuses.filter(s => s === 'skip-empty').length,
        skipExisting: rowStatuses.filter(s => s === 'skip-existing').length,
    }), [rowStatuses]);

    const skippedLineNumbers = React.useMemo(
        () => rowStatuses
            .map((s, i) => s === 'skip-empty' || s === 'skip-existing' ? result.rowLineNumbers[i] : null)
            .filter((n): n is number => n !== null),
        [rowStatuses, result.rowLineNumbers],
    );

    const currentStatus = rowStatuses[currentIndex];
    const isSkipped = currentStatus === 'skip-empty' || currentStatus === 'skip-existing';
    const currentName = previewData?.name?.trim() ?? '';
    const currentNameExists = currentName
        ? existingClients.some(c => c.name?.trim().toLowerCase() === currentName.toLowerCase())
        : false;

    if (!previewData) return null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
            {/* Summary */}
            <div style={{
                display: 'flex',
                gap: 'var(--spacer-M)',
                padding: 'var(--spacer-S)',
                background: 'var(--bg-light)',
                borderRadius: '6px',
                fontSize: '0.85rem',
                flexWrap: 'wrap',
            }}>
                <span style={{ color: STATUS_COLORS.create }}>
                    <strong>{counts.create}</strong> {t(TK.importClientsWillCreate)}
                </span>
                <span style={{ color: STATUS_COLORS.update }}>
                    <strong>{counts.update}</strong> {t(TK.importClientsWillUpdate)}
                </span>
                <span style={{ color: STATUS_COLORS['skip-empty'] }}>
                    <strong>{counts.skipEmpty}</strong> {t(TK.importClientsWillSkip)}
                </span>
                <span style={{ position: 'relative' }}>
                    <span
                        onClick={(counts.skipEmpty + counts.skipExisting) > 0 ? () => setShowSkippedList(v => !v) : undefined}
                        style={{
                            color: STATUS_COLORS['skip-existing'],
                            cursor: (counts.skipEmpty + counts.skipExisting) > 0 ? 'pointer' : 'default',
                            textDecoration: (counts.skipEmpty + counts.skipExisting) > 0 ? 'underline dotted' : 'none',
                        }}
                    >
                        <strong>{counts.skipExisting}</strong> {t(TK.importClientsWillSkipExisting)}
                    </span>
                    {showSkippedList && (counts.skipEmpty + counts.skipExisting) > 0 && (
                        <>
                            {/* Invisible overlay — click outside to close */}
                            <div
                                style={{ position: 'fixed', inset: 0, zIndex: 200 }}
                                onClick={() => setShowSkippedList(false)}
                            />
                            <div style={{
                                position: 'absolute',
                                top: 'calc(100% + 6px)',
                                left: 0,
                                zIndex: 201,
                                background: 'white',
                                border: 'var(--border-thin) solid var(--appLigthGrey)',
                                borderRadius: '6px',
                                padding: 'var(--spacer-S)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                minWidth: '200px',
                                maxWidth: '320px',
                            }}>
                                <div style={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: 'var(--text-muted)',
                                    marginBottom: 'var(--spacer-XS)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                }}>
                                    {t(TK.importClientsWillSkip)} / {t(TK.importClientsWillSkipExisting)}
                                </div>
                                <div style={{
                                    maxHeight: '180px',
                                    overflowY: 'auto',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 'var(--spacer-XXS)',
                                }}>
                                    {skippedLineNumbers.slice(0, MAX_SKIPPED_SHOWN).map(n => (
                                        <span
                                            key={n}
                                            style={{
                                                background: 'var(--bg-light)',
                                                border: 'var(--border-thin) solid var(--appLigthGrey)',
                                                borderRadius: '4px',
                                                padding: '1px var(--spacer-XS)',
                                                fontSize: '0.78rem',
                                                fontVariantNumeric: 'tabular-nums',
                                            }}
                                        >
                                            {n}
                                        </span>
                                    ))}
                                </div>
                                {skippedLineNumbers.length > MAX_SKIPPED_SHOWN && (
                                    <div style={{
                                        marginTop: 'var(--spacer-XS)',
                                        fontSize: '0.75rem',
                                        color: 'var(--text-muted)',
                                        fontStyle: 'italic',
                                    }}>
                                        {t(TK.importClientsAndNMore).replace('{n}', String(skippedLineNumbers.length - MAX_SKIPPED_SHOWN))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </span>
            </div>

            {/* Navigation bar */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacer-S)',
                justifyContent: 'center',
            }}>
                <button
                    onClick={() => navigate(-1)}
                    disabled={currentIndex === 0}
                    style={navBtnStyle}
                    title={t(TK.back)}
                >
                    ←
                </button>
                <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)', minWidth: '80px', textAlign: 'center' }}>
                    {currentIndex + 1} / {total}
                </span>
                <div style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: STATUS_COLORS[currentStatus],
                    flexShrink: 0,
                }} />
                <button
                    onClick={() => navigate(1)}
                    disabled={currentIndex === total - 1}
                    style={navBtnStyle}
                    title={t(TK.next)}
                >
                    →
                </button>
            </div>

            {isSkipped && (
                <Alert color="warning" style={{ marginBottom: 0 }}>
                    {currentName && options.skipExistingOnly && currentNameExists
                        ? t(TK.importClientsSkippedExistingRowWarning)
                        : t(TK.importClientsSkippedRowWarning)}
                </Alert>
            )}

            {/* ClientForm preview — pointer-events disabled to prevent edits */}
            <div style={{ pointerEvents: 'none', userSelect: 'none' }}>
                <ClientForm
                    data={previewData}
                    hasChanged={false}
                    type="create"
                    canManageClients={false}
                    hideActions
                    onChange={() => {}}
                    onSave={() => {}}
                />
            </div>
        </div>
    );
};

const navBtnStyle: React.CSSProperties = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    border: 'var(--border-thin) solid var(--appLigthGrey)',
    background: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1rem',
    color: 'var(--text-body)',
};

export default PreviewStep;
